/**
 * What goes into a conversation beside typed words: a file you attach, and speech — the composer
 * listens, shows what it hears softly, and puts the words in the box for you to read before anything
 * is sent.
 */
import { useEffect, useRef, useState } from 'react'
import { Check, X } from 'lucide-react'
import type { Attachment } from '@shared/types'
import { IconBtn, cx } from './kit'
import type { Pending } from './store'

const kindOf = (name: string) => {
  const ext = name.split('.').pop()?.toLowerCase() ?? ''
  return ({ pdf: 'PDF', xlsx: 'XLS', xls: 'XLS', csv: 'CSV', png: 'IMG', jpg: 'IMG', jpeg: 'IMG', webp: 'IMG', gif: 'IMG', docx: 'DOC', pptx: 'PPT', md: 'MD', txt: 'TXT', json: 'JSON' } as Record<string, string>)[ext] ?? (ext.slice(0, 4).toUpperCase() || 'FILE')
}

/** A file riding on a message of yours, or waiting in the composer (then it can be taken off). */
export function FileChip({ name, onRemove, onPanel }: { name: string; onRemove?: () => void; onPanel?: boolean }) {
  return (
    <span className={cx('flex h-8 max-w-full items-center gap-2 rounded-[8px] border pl-[5px] text-[12px] tracking-normal text-ws-fg-2', onPanel ? 'border-ws-rule bg-ws-raise' : 'border-ws-edge bg-ws-panel', onRemove ? 'pr-1' : 'pr-[10px]')}>
      <span className="flex h-[22px] min-w-[30px] shrink-0 items-center justify-center rounded-[5px] bg-ws-bg px-1 text-[9.5px] font-semibold leading-none tracking-[0.03em] text-ws-fg-2" aria-hidden>
        {kindOf(name)}
      </span>
      <span className="min-w-0 truncate">{name}</span>
      {onRemove && (
        <button type="button" aria-label={`Remove ${name}`} onClick={onRemove} className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[5px] text-ws-icon transition-colors hover:bg-[#2a2a2a] hover:text-ws-fg">
          <X size={12} strokeWidth={2.2} />
        </button>
      )}
    </span>
  )
}

const TEXT_EXT = /\.(txt|md|markdown|csv|tsv|json|log|js|ts|jsx|tsx|py|html|xml|ya?ml|sql|sh|css)$/i
const MAX_FILE = 50 * 1024 * 1024
const dataUrl = (file: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(r.result as string)
    r.onerror = () => reject(new Error('The file could not be read'))
    r.readAsDataURL(file)
  })
/** A picked file as the server takes it: a picture is seen, text is read, anything else is handed to the agent's machine. */
export async function toPending(file: File): Promise<Pending> {
  if (file.size > MAX_FILE) throw new Error(`${file.name} is over 50 MB`)
  let attachment: Attachment
  if (file.type.startsWith('image/')) attachment = { name: file.name, mime: file.type, kind: 'image', dataUrl: await dataUrl(file) }
  else if (TEXT_EXT.test(file.name) || file.type.startsWith('text/') || file.type === 'application/json') attachment = { name: file.name, mime: file.type || 'text/plain', kind: 'text', text: await file.text() }
  else attachment = { name: file.name, mime: file.type || 'application/octet-stream', kind: 'binary', dataUrl: await dataUrl(file) }
  return { name: file.name, size: file.size, attachment }
}

/* ── speaking ────────────────────────────────────────────────────────── */

/** The sound as it comes: thin bars entering from the right, the newest last. */
function Wave({ level }: { level: React.RefObject<() => number> }) {
  const ref = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    const c = ref.current
    const ctx = c?.getContext('2d')
    if (!c || !ctx) return
    const heard: number[] = []
    let raf = 0
    let last = 0
    const draw = (t: number) => {
      raf = requestAnimationFrame(draw)
      if (t - last < 60) return
      last = t
      const dpr = window.devicePixelRatio || 1
      const w = c.clientWidth
      const h = c.clientHeight
      if (c.width !== Math.round(w * dpr)) (c.width = Math.round(w * dpr)), (c.height = Math.round(h * dpr))
      const n = Math.floor((w + 2) / 4)
      heard.push(level.current())
      while (heard.length > n) heard.shift()
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, w, h)
      for (let i = 0; i < n; i++) {
        const v = heard[heard.length - n + i]
        const bar = v == null ? 2 : Math.max(2, Math.round(v * h))
        ctx.fillStyle = v == null ? '#303030' : '#b8b8b8'
        ctx.beginPath()
        ctx.roundRect(i * 4, (h - bar) / 2, 2, bar, 1)
        ctx.fill()
      }
    }
    raf = requestAnimationFrame(draw)
    return () => cancelAnimationFrame(raf)
  }, [level])
  return <canvas ref={ref} className="h-6 min-w-0 flex-1" aria-hidden />
}

/** Send what was recorded so far to be written down. */
async function transcribe(audio: Blob, signal?: AbortSignal): Promise<string> {
  const form = new FormData()
  form.append('file', audio, audio.type.includes('mp4') ? 'speech.m4a' : 'speech.webm')
  const res = await fetch('/api/stt', { method: 'POST', body: form, signal })
  const body = (await res.json().catch(() => ({}))) as { text?: string; error?: string }
  if (!res.ok) throw new Error(body.error ?? `Could not write it down (${res.status})`)
  return (body.text ?? '').trim()
}

/**
 * The composer while you speak. What is heard appears softly as it comes — it is not yet yours. The
 * tick puts it into the box, where you read and change it; nothing is ever sent by speaking alone.
 */
export function Dictation({ base, dock, onDone, onCancel }: { base: string; dock?: boolean; onDone: (heard: string) => void; onCancel: () => void }) {
  const [heard, setHeard] = useState('')
  const [secs, setSecs] = useState(0)
  const [state, setState] = useState<'starting' | 'listening' | 'writing' | 'blocked'>('starting')
  const [problem, setProblem] = useState('')
  const level = useRef<() => number>(() => 0)
  const chunks = useRef<Blob[]>([])
  const rec = useRef<MediaRecorder | null>(null)
  const stopAll = useRef<() => void>(() => {})
  const busy = useRef(false)

  useEffect(() => {
    let live = true
    let interim = 0
    let tick = 0
    void (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        if (!live) return stream.getTracks().forEach((t) => t.stop())
        const ctx = new AudioContext()
        const node = ctx.createAnalyser()
        node.fftSize = 256
        ctx.createMediaStreamSource(stream).connect(node)
        const buf = new Uint8Array(node.fftSize)
        level.current = () => {
          node.getByteTimeDomainData(buf)
          let sum = 0
          for (const v of buf) sum += ((v - 128) / 128) ** 2
          return Math.min(1, Math.sqrt(sum / buf.length) * 4)
        }
        const mime = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find((m) => MediaRecorder.isTypeSupported(m))
        const r = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined)
        r.ondataavailable = (e) => e.data.size && chunks.current.push(e.data)
        r.start(1000)
        rec.current = r
        stopAll.current = () => (stream.getTracks().forEach((t) => t.stop()), void ctx.close().catch(() => {}))
        setState('listening')
        tick = window.setInterval(() => setSecs((x) => x + 1), 1000)
        // Every few seconds what was said so far is written down softly, so you see it is being understood.
        interim = window.setInterval(() => {
          if (busy.current || !chunks.current.length) return
          busy.current = true
          transcribe(new Blob(chunks.current, { type: r.mimeType }))
            .then((t) => live && t && setHeard(t))
            .catch(() => {})
            .finally(() => (busy.current = false))
        }, 3500)
      } catch {
        if (live) (setState('blocked'), setProblem('The microphone is not available to this page. Allow it in the browser, then try again.'))
      }
    })()
    return () => {
      live = false
      window.clearInterval(interim)
      window.clearInterval(tick)
      if (rec.current?.state === 'recording') rec.current.stop()
      stopAll.current()
    }
  }, [])

  const finish = () => {
    const r = rec.current
    if (!r || state !== 'listening') return onDone(heard)
    setState('writing')
    r.onstop = () => {
      stopAll.current()
      transcribe(new Blob(chunks.current, { type: r.mimeType }))
        .then((t) => onDone(t || heard))
        .catch((e) => (heard ? onDone(heard) : (setState('blocked'), setProblem(e instanceof Error ? e.message : 'It could not be written down'))))
    }
    r.stop()
  }
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') (e.stopPropagation(), onCancel())
      else if (e.key === 'Enter') (e.preventDefault(), finish())
    }
    document.addEventListener('keydown', key, true)
    return () => document.removeEventListener('keydown', key, true)
  })
  return (
    <>
      <p className={cx('m-0 whitespace-pre-wrap px-4 pt-[18px] leading-[21px] tracking-normal', dock ? 'min-h-[52px] pb-1' : 'min-h-[85px] pb-2')} aria-live="polite">
        {base && <span className="text-ws-fg">{base} </span>}
        <span className="text-ws-fg-3">{heard}</span>
        {!heard && <span className="text-ws-fg-4">{state === 'blocked' ? problem : state === 'starting' ? 'Waiting for the microphone…' : state === 'writing' ? 'Writing it down…' : base ? '' : 'Listening…'}</span>}
        {state === 'listening' && <span className="ml-[2px] inline-block h-[14px] w-[2px] translate-y-[2px] animate-ws-caret bg-ws-fg-3" />}
      </p>
      <div className="mx-2 mb-[9px] flex h-8 items-center gap-3">
        <IconBtn icon={X} label="Discard what was heard" onPanel onClick={onCancel} />
        <Wave level={level} />
        <span className="w-8 shrink-0 text-right text-[12px] tabular-nums tracking-normal text-ws-fg-3">
          {Math.floor(secs / 60)}:{String(secs % 60).padStart(2, '0')}
        </span>
        <button type="button" aria-label="Put it in the box" title="Put it in the box" disabled={state === 'writing' || state === 'starting'} onClick={finish} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] bg-white text-black disabled:opacity-40">
          <Check size={16} strokeWidth={2.4} />
        </button>
      </div>
    </>
  )
}
